// src/features/blog/components/BlogManager.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Snackbar,
  Alert,
  Paper,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import { useBlog } from "../hooks/useBlog";
import BlogStats from "./BlogStats";
import BlogList from "./BlogList";
import BlogForm from "./BlogForm";
import BlogFilter from "./BlogFilter";

const BlogManager = () => {
  const {
    posts,
    loading,
    error,
    success,
    filters,
    stats,
    getPosts,
    createNewPost,
    updateExistingPost,
    deleteExistingPost,
    publishExistingPost,
    uploadImage,
    updateFilters,
    clearAllFilters,
    clearErrorMessage,
    clearSuccessMessage,
  } = useBlog();

  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    getPosts();
  }, [filters]);

  useEffect(() => {
    if (error || success) {
      setSnackbarOpen(true);
    }
  }, [error, success]);

  const handleCreatePost = async (data) => {
    try {
      await createNewPost(data);
      setShowForm(false);
      getPosts();
    } catch (err) {
      console.error("Create failed:", err);
    }
  };

  const handleUpdatePost = async (data) => {
    try {
      await updateExistingPost(editingPost.id, data);
      setShowForm(false);
      setEditingPost(null);
      getPosts();
    } catch (err) {
      console.error("Update failed:", err);
    }
  };

  const handleDeletePost = async (id) => {
    if (window.confirm("Delete this blog post?")) {
      try {
        await deleteExistingPost(id);
        getPosts();
      } catch (err) {
        console.error("Delete failed:", err);
      }
    }
  };

  const handlePublishPost = async (id) => {
    try {
      await publishExistingPost(id);
      getPosts();
    } catch (err) {
      console.error("Publish failed:", err);
    }
  };

  const handleImageUpload = async (file) => {
    try {
      const result = await uploadImage({ file });
      return result;
    } catch (err) {
      console.error("Upload failed:", err);
      throw err;
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
    clearErrorMessage();
    clearSuccessMessage();
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1" fontWeight="bold">
          Blog Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setEditingPost(null);
            setShowForm(true);
          }}
          sx={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          }}
        >
          New Post
        </Button>
      </Box>

      {/* Stats */}
      <BlogStats stats={stats} />

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <BlogFilter
          filters={filters}
          onFilterChange={updateFilters}
          onClear={clearAllFilters}
        />
      </Paper>

      {/* List */}
      <BlogList
        posts={posts}
        loading={loading}
        onEdit={(post) => {
          setEditingPost(post);
          setShowForm(true);
        }}
        onDelete={handleDeletePost}
        onPublish={handlePublishPost}
        onPreview={() => {}}
      />

      {/* Create/Edit Dialog */}
      <Dialog
        open={showForm}
        onClose={() => {
          setShowForm(false);
          setEditingPost(null);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {editingPost ? "Edit Blog Post" : "Create New Blog Post"}
        </DialogTitle>
        <DialogContent>
          <BlogForm
            initialData={editingPost}
            onSubmit={editingPost ? handleUpdatePost : handleCreatePost}
            onCancel={() => {
              setShowForm(false);
              setEditingPost(null);
            }}
            loading={loading}
            onImageUpload={handleImageUpload}
          />
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={error ? "error" : "success"}
          variant="filled"
        >
          {error || success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BlogManager;
